import type { Interpolation, Theme } from '@emotion/react';
import { COLOR_PALETTE } from '@inflearn/ds-react';
import { Box, NavLink, type NavLinkProps } from '@mantine/core';
import { useRouter } from 'next/router';
import { useSidebarLinks } from './useSidebarLinks';

export function Sidebar({
  styles,
}: {
  styles?: {
    root?: Interpolation<Theme>;
  };
}) {
  const router = useRouter();
  const sidebarLinks = useSidebarLinks();

  return (
    <Box css={styles?.root}>
      {sidebarLinks.map((link) => {
        const isActive = link.isActive(router.asPath);
        return (
          <SidebarNavLink
            key={link.label}
            {...link}
            active={isActive}
            isExternal={link.isExternal}
          />
        );
      })}
    </Box>
  );
}

function SidebarNavLink(props: NavLinkProps & { isExternal?: boolean; href: string }) {
  const router = useRouter();

  const navLinkStyles = {
    root: {
      padding: '12px 16px',
      height: '49px',
      borderRadius: '8px',
    },
    label: {
      fontSize: '16px',
      fontWeight: 400,
    },
    icon: {
      color: props.active ? COLOR_PALETTE.infgreen[8] : COLOR_PALETTE.gray[7],
    },
  };
  const { isExternal, href, ...restProps } = props;

  if (isExternal) {
    return <NavLink component="a" variant="light" styles={navLinkStyles} {...props} />;
  }

  return (
    <NavLink
      component="a"
      target={isExternal ? '_blank' : '_self'}
      href={href}
      variant="light"
      styles={navLinkStyles}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        // Allow opening in new tab with Cmd/Ctrl + click
        if (e.metaKey || e.ctrlKey) {
          return;
        }
        e.preventDefault();
        router.push(href);
      }}
      {...restProps}
    />
  );
}
